import { Routes, Route, Outlet, Navigate, useNavigate } from 'react-router-dom';
import { MainLayout } from '@/components/layout';
import { AuthGuard } from '@/components/auth';
import { AuthProvider } from '@/lib/auth';
import { TenantProvider } from '@/contexts/TenantContext';
import { DashboardsGovernancePage } from '@/features/dashboarding/DashboardsGovernancePage';

import { AccessDeniedModal } from 'shared/components/errors';
import { useErrorHandler } from 'shared/hooks';
import {
  DashboardPage,
  RecycleBinPage,
  AgentsListPage,
  AgentEditorPage,
  HivesListPage,
  HiveDetailPage,
  DelegationMonitorPage,
  HiveCreatePage,
  HandoffCreatePage,
  CollectionsListPage,
  CollectionFormPage,
  CollectionDocumentsPage,
  IngestPage,
  TaxonomiesPage,
  DocumentsListPage,
  KnowledgeBasesPage,
  KnowledgeBaseDetailPage,
  SettingsPage,
  PreferencesPage,
  PermissionsPage,
  StoragePage,
  KnowledgeSettingsPage,
  GuardrailsSettingsPage,
  ChatSettingsPage,
  DataGovernancePage,
  SettingsIntegrationsPage,
  TenantProvidersPage,
  PlatformProvidersPage,
  WebCapabilityPage,
  ModelManagementPage,
  LlmCatalogPage,
  PlatformDrLlmPolicyPage,
  TenantLlmPolicyPage,
  UsageDashboardPage,
  ModelUsageGovernancePage,
  EmailDashboardPage,
  SystemTenantsPage,
  ChannelsListPage,
  ChannelWizardPage,
  TeamsBotWizardPage,
  SlackWizardPage,
  WhatsAppWizardPage,
  TelegramWizardPage,
  ChannelDetailPage,
  TokenSavingsPage,
} from '@/pages';
import {
  SkillsCatalogPage,
  SkillDetailPage,
} from '@/pages/skills';
import {
  MCPServersListPage,
  MCPServerCreatePage,
  MCPServerDetailPage,
  MCPCatalogPage,
  MCPTemplateDetailPage,
  RunnersListPage,
  RunnerDetailPage,
  RunnerPolicyPage,
  PolicyGlobalPage,
  PolicyProfilesPage,
  AgentDownloadPage,
  SystemTemplatesPage,
  SystemTemplateCreatePage,
  SystemTemplateEditPage,
} from '@/pages/integrations';
import {
  DataSourcesListPage,
  DataSourceFormPage,
  DataSourceWizardPage,
  DataSourceDetailPage,
  OAuthCallbackPage,
  GitHubInstalledPage,
} from '@/features/datasources';
import { CACMarketplacePage, CACPublishPage, CACApprovalsPage, LicenseTemplatesPage, PartnerProfilePage, PartnerAdminPage, RevenuePage } from '@/pages/cac';
import { SecretVaultPage } from '@/features/secrets';
import { TelemetrySettingsPage } from '@/features/telemetry';
import { MyLicensePage } from '@/features/license';
import { SettingsLayout } from '@/pages/settings/SettingsLayout';
import { SystemAdminGuard } from '@/components/SystemAdminGuard';
import { ModuleAccessGuard } from '@/components/ModuleAccessGuard';
import { CACApprovalsGuard } from '@/components/CACApprovalsGuard';
import {
  LoginPage,
  MfaSetupPage,
  MfaChallengePage,
  ErrorPage,
} from '@/pages/auth';
import { SecurityAlertsPage } from '@/features/security-alerts';
import {
  FrontierEventsPage,
  FrontierGuard,
  FrontierInboxPage,
  FrontierOverviewPage,
  FrontierRoutinesPage,
  FrontierRunDetailPage,
  FrontierRunsPage,
} from '@/features/frontier';
import {
  ModelIntelGuard,
  ModelPricingGovernancePage,
  ModelPricingPage,
  PanoramaPage,
  ComparatorPage,
  ModelCardPage,
  FunctionViewPage,
  SystemPricingOverviewPage,
} from '@/features/model-intelligence';
import {
  GatewayDevelopersPage,
  GatewayGuard,
  GatewayIdePage,
  GatewayPolicyPage,
} from '@/features/ai-gateway';

// =============================================================================
// Error Handler Wrapper
// =============================================================================

function ErrorHandlerWrapper({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const { showAccessDenied, dismissError } = useErrorHandler();

  const handleGoBack = () => {
    dismissError();
    navigate(-1);
  };

  return (
    <>
      {children}
      <AccessDeniedModal
        isOpen={showAccessDenied}
        onClose={dismissError}
        onGoBack={handleGoBack}
        supportEmail="support@atlas.local"
      />
    </>
  );
}

// =============================================================================
// Protected Routes Wrapper
// =============================================================================

function ProtectedRoutes() {
  return (
    <AuthProvider requireAuth={true}>
      <AuthGuard requireMfa={true}>
        <TenantProvider>
            <ErrorHandlerWrapper>
              <Outlet />
            </ErrorHandlerWrapper>
        </TenantProvider>
      </AuthGuard>
    </AuthProvider>
  );
}

// =============================================================================
// App Component with Routes
// =============================================================================

export function App() {
  return (
    <Routes>
      {/* Public auth routes - no auth required */}
      <Route path="/auth/login" element={<LoginPage />} />
      <Route path="/auth/mfa/setup" element={<MfaSetupPage />} />
      <Route path="/auth/mfa/challenge" element={<MfaChallengePage />} />
      <Route path="/auth/error" element={<ErrorPage />} />
      <Route path="/knowledge/datasources/oauth/callback" element={<OAuthCallbackPage />} />
      <Route path="/mcp/setup/callback" element={<OAuthCallbackPage />} />
      
      {/* Protected routes - require auth + MFA */}
      <Route element={<ProtectedRoutes />}>
        <Route path="/" element={<MainLayout />}>
          {/* Dashboard */}
          <Route index element={<DashboardPage />} />
          <Route path="dashboards-governance" element={<DashboardsGovernancePage />} />
          
          {/* Agents */}
          <Route path="agents" element={<AgentsListPage />} />
          <Route path="agents/new" element={<AgentEditorPage />} />
          <Route path="agents/:id" element={<AgentEditorPage />} />
          
          {/* Hives (Multi-Agent) */}
          <Route path="agents/hives" element={<HivesListPage />} />
          <Route path="agents/hives/new" element={<HiveCreatePage />} />
          <Route path="agents/hives/:hiveId/handoffs/new" element={<HandoffCreatePage />} />
          <Route path="agents/hives/:hiveId/handoffs/:handoffId/edit" element={<HandoffCreatePage />} />
          <Route path="agents/hives/:id" element={<HiveDetailPage />} />
          <Route path="agents/delegations" element={<DelegationMonitorPage />} />
          
          {/* Skills */}
          <Route path="skills" element={<SkillsCatalogPage />} />
          <Route path="skills/:slug" element={<SkillDetailPage />} />
          
          {/* Knowledge */}
          <Route path="knowledge" element={<CollectionsListPage />} />
          <Route path="knowledge/bases" element={<KnowledgeBasesPage />} />
          <Route path="knowledge/bases/:id" element={<KnowledgeBaseDetailPage />} />
          <Route path="knowledge/collections" element={<CollectionsListPage />} />
          <Route path="knowledge/collections/new" element={<CollectionFormPage />} />
          <Route path="knowledge/collections/:id" element={<CollectionDocumentsPage />} />
          <Route path="knowledge/collections/:id/edit" element={<CollectionFormPage />} />
          <Route path="knowledge/collections/:id/ingest" element={<IngestPage />} />
          <Route path="knowledge/taxonomies" element={<TaxonomiesPage />} />
          <Route path="knowledge/documents" element={<DocumentsListPage />} />
          <Route path="knowledge/ingest" element={<IngestPage />} />
          
          {/* Data Sources */}
          <Route path="knowledge/datasources" element={<DataSourcesListPage />} />
          <Route path="knowledge/datasources/new" element={<DataSourceWizardPage />} />
          <Route path="knowledge/datasources/github/installed" element={<GitHubInstalledPage />} />
          <Route path="knowledge/datasources/:id" element={<DataSourceDetailPage />} />
          <Route path="knowledge/datasources/:id/edit" element={<DataSourceFormPage />} />
          
          {/* Integrations (MCP) */}
          <Route path="integrations" element={<MCPServersListPage />} />
          <Route path="integrations/new" element={<MCPServerCreatePage />} />
          <Route path="integrations/catalog" element={<MCPCatalogPage />} />
          <Route path="integrations/catalog/:slug" element={<MCPTemplateDetailPage />} />
          <Route path="integrations/runners" element={<RunnersListPage />} />
          <Route path="integrations/runners/:id" element={<RunnerDetailPage />} />
          <Route path="integrations/runners/:id/policy" element={
            <ModuleAccessGuard module="runner" level="admin" redirectTo="/integrations/runners">
              <RunnerPolicyPage />
            </ModuleAccessGuard>
          } />
          <Route path="integrations/runners/policies" element={
            <ModuleAccessGuard module="runner" level="admin" redirectTo="/integrations/runners">
              <PolicyGlobalPage />
            </ModuleAccessGuard>
          } />
          <Route path="integrations/runners/profiles" element={
            <ModuleAccessGuard module="runner" level="admin" redirectTo="/integrations/runners">
              <PolicyProfilesPage />
            </ModuleAccessGuard>
          } />
          <Route path="integrations/download-agent" element={<AgentDownloadPage />} />
          <Route path="integrations/templates" element={
            <SystemAdminGuard>
              <SystemTemplatesPage />
            </SystemAdminGuard>
          } />
          <Route path="integrations/templates/new" element={
            <SystemAdminGuard>
              <SystemTemplateCreatePage />
            </SystemAdminGuard>
          } />
          <Route path="integrations/templates/:slug/edit" element={
            <SystemAdminGuard>
              <SystemTemplateEditPage />
            </SystemAdminGuard>
          } />
          <Route path="integrations/:id" element={<MCPServerDetailPage />} />

          {/* Kaviar Marketplace */}
          <Route path="marketplace" element={<CACMarketplacePage />} />
          <Route path="marketplace/publish" element={<CACPublishPage />} />
          <Route path="marketplace/approvals" element={
            <CACApprovalsGuard>
              <CACApprovalsPage />
            </CACApprovalsGuard>
          } />
          <Route path="marketplace/license-templates" element={
            <SystemAdminGuard>
              <LicenseTemplatesPage />
            </SystemAdminGuard>
          } />
          <Route path="marketplace/partner-profile" element={<PartnerProfilePage />} />
          <Route path="marketplace/partners" element={
            <CACApprovalsGuard>
              <PartnerAdminPage />
            </CACApprovalsGuard>
          } />
          <Route path="marketplace/revenue" element={<RevenuePage />} />

          {/* Channels Integrations */}
          <Route path="channels" element={<ChannelsListPage />} />
          <Route path="channels/new" element={<ChannelWizardPage />} />
          <Route path="channels/new/teams" element={<TeamsBotWizardPage />} />
          <Route path="channels/new/slack" element={<SlackWizardPage />} />
          <Route path="channels/new/whatsapp" element={<WhatsAppWizardPage />} />
          <Route path="channels/new/telegram" element={<TelegramWizardPage />} />
          <Route path="channels/:id" element={<ChannelDetailPage />} />
          
          {/* Usage Dashboard */}
          <Route path="usage" element={<UsageDashboardPage />} />
          <Route path="analytics" element={<UsageDashboardPage />} />

          {/* Mi licencia (REQ-BIL-018) — read-only, gated by License#view */}
          <Route path="license" element={<MyLicensePage />} />

          {/* Email SLO Dashboard */}
          <Route path="email-slo" element={<EmailDashboardPage />} />
          
          {/* Secret Vault */}
          <Route path="secrets" element={<SecretVaultPage />} />
          
          {/* Data Governance */}
          <Route path="data-governance" element={<DataGovernancePage />} />
          
          {/* Settings (wrapped in SettingsLayout for secondary sidebar) */}
          <Route path="settings" element={<SettingsLayout />}>
            <Route index element={<SettingsPage />} />
            <Route path="models" element={<ModelManagementPage />} />
            <Route path="providers" element={<TenantProvidersPage />} />
            <Route path="platform-providers" element={
              <SystemAdminGuard>
                <PlatformProvidersPage />
              </SystemAdminGuard>
            } />
            <Route path="web-capability" element={
              <SystemAdminGuard>
                <WebCapabilityPage />
              </SystemAdminGuard>
            } />
            <Route path="preferences" element={<PreferencesPage />} />
            <Route path="knowledge" element={<KnowledgeSettingsPage />} />
            <Route path="guardrails" element={<GuardrailsSettingsPage />} />
            <Route path="chat" element={<ChatSettingsPage />} />
            <Route path="data-governance" element={<DataGovernancePage />} />
            <Route path="permissions" element={<PermissionsPage />} />
            <Route path="integrations" element={<SettingsIntegrationsPage />} />
            <Route path="storage" element={<StoragePage />} />
            <Route path="llm-catalog" element={<LlmCatalogPage />} />
            <Route path="dr-llm-policy" element={
              <SystemAdminGuard>
                <PlatformDrLlmPolicyPage />
              </SystemAdminGuard>
            } />
            <Route path="llm-policy" element={<TenantLlmPolicyPage />} />
            <Route path="telemetry" element={<TelemetrySettingsPage />} />
          </Route>
          
          {/* Security Alerts */}
          <Route path="security/alerts" element={<SecurityAlertsPage />} />

          {/* Model Intelligence — superficies de consulta y decisión (Sprint 285 G
              precios; Sprint 286 D panorama/comparador; gated por Module:model-intel#view) */}
          <Route
            path="model-intel/panorama"
            element={
              <ModelIntelGuard>
                <PanoramaPage />
              </ModelIntelGuard>
            }
          />
          <Route
            path="model-intel/compare"
            element={
              <ModelIntelGuard>
                <ComparatorPage />
              </ModelIntelGuard>
            }
          />
          <Route
            path="model-intel/functions"
            element={
              <ModelIntelGuard>
                <FunctionViewPage />
              </ModelIntelGuard>
            }
          />
          <Route
            path="model-intel/functions/:code"
            element={
              <ModelIntelGuard>
                <FunctionViewPage />
              </ModelIntelGuard>
            }
          />
          <Route
            path="model-intel/model/:modelRef"
            element={
              <ModelIntelGuard>
                <ModelCardPage />
              </ModelIntelGuard>
            }
          />
          <Route
            path="model-intel/pricing"
            element={
              <ModelIntelGuard>
                <ModelPricingPage />
              </ModelIntelGuard>
            }
          />
          <Route path="governance/token-savings" element={<TokenSavingsPage />} />
          <Route
            path="governance/model-usage"
            element={<ModelUsageGovernancePage />}
          />
          <Route
            path="governance/model-pricing"
            element={
              <ModelIntelGuard>
                <ModelPricingGovernancePage />
              </ModelIntelGuard>
            }
          />

          {/* AI Gateway Studio (Sprint 365 A; gated by Module:ai_gateway#view) */}
          <Route
            path="ia-gateway"
            element={
              <GatewayGuard>
                <GatewayDevelopersPage />
              </GatewayGuard>
            }
          />
          <Route
            path="ia-gateway/ide"
            element={
              <GatewayGuard>
                <GatewayIdePage />
              </GatewayGuard>
            }
          />
          <Route
            path="ia-gateway/policy"
            element={
              <GatewayGuard>
                <GatewayPolicyPage />
              </GatewayGuard>
            }
          />
          {/* Compatibilidad: deep links previos sin 404 (Sprint 365 A). */}
          <Route path="developers" element={<Navigate to="/ia-gateway" replace />} />
          <Route
            path="developers/policy"
            element={<Navigate to="/ia-gateway/policy" replace />}
          />

          {/* Frontier Agents (Sprint 282 G; gated by Module:frontier#view) */}
          <Route
            path="frontier"
            element={
              <FrontierGuard>
                <FrontierOverviewPage />
              </FrontierGuard>
            }
          />
          <Route
            path="frontier/routines"
            element={
              <FrontierGuard>
                <FrontierRoutinesPage />
              </FrontierGuard>
            }
          />
          <Route
            path="frontier/runs"
            element={
              <FrontierGuard>
                <FrontierRunsPage />
              </FrontierGuard>
            }
          />
          <Route
            path="frontier/runs/:runId"
            element={
              <FrontierGuard>
                <FrontierRunDetailPage />
              </FrontierGuard>
            }
          />
          <Route
            path="frontier/inbox"
            element={
              <FrontierGuard>
                <FrontierInboxPage />
              </FrontierGuard>
            }
          />
          <Route
            path="frontier/events"
            element={
              <FrontierGuard>
                <FrontierEventsPage />
              </FrontierGuard>
            }
          />
          {/* Compatibilidad: la antigua vista de Errores pasa a Eventos */}
          <Route path="frontier/errors" element={<Navigate to="/frontier/events" replace />} />
          
          {/* Recycle Bin */}
          <Route path="recycle-bin" element={<RecycleBinPage />} />

          {/* System Admin Routes (SA-19: same guard as other system routes) */}
          <Route path="system/tenants" element={
            <SystemAdminGuard>
              <SystemTenantsPage />
            </SystemAdminGuard>
          } />
          <Route path="system/model-pricing" element={
            <SystemAdminGuard>
              <SystemPricingOverviewPage />
            </SystemAdminGuard>
          } />
        </Route>
      </Route>
    </Routes>
  );
}

export default App;
